import React, { useState, useRef } from 'react';
import Image from 'next/image';
import gsap from "gsap";
import { useGSAP } from '@gsap/react';


interface PrincipleText {
  text: string;
}

interface PrincipleItem {
  name: string;
  image: { url: string };
  text: PrincipleText[];
}

interface AboutPrinciplesProps {
  items: PrincipleItem[];
}



const AboutPrinciples = ({ items }: AboutPrinciplesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const sectionRef = useRef(null)
  const imagesWrapper = useRef(null)
  const containerRef = useRef(null)
  const textRef = useRef(null)
  const titleRef = useRef(null)

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1280px)", () => {


        gsap.fromTo(titleRef.current, 
          { y: "-33rem" }, 
          {
            y: 0,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top bottom",
              end: "top 15%",
              scrub: 0.1,
            }
          }
        )

        gsap.fromTo(textRef.current, 
          { y: "-35rem" }, 
          {
            y: 0,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top bottom",
              end: "top 15%",
              scrub: 0.1,
            }
          }
        )
      

      gsap.fromTo(containerRef.current, {
        y: '13rem'
      },
          {
        y: 0,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom", 
          end: "top 60%",  
          scrub: 0.1,  
        },
      });

      gsap.utils.toArray<HTMLElement>(".parallax-image").forEach((img) => {
        gsap.fromTo(img, {
          y: '-20rem'
        },
           {
          y: '20rem', // how much the image moves
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "80% top", 
            scrub: 0.1, 
          },
        });
      });
    });

    return () => mm.revert(); // cleanup
  }, {scope: sectionRef});


  return (
    <section ref={sectionRef} className="container-max pt-[8rem] md:pt-[10rem] lg:pt-[15rem]  pb-[4rem] md:pb-[6rem] lg:pb-[8rem]   container-padding">
      <div ref={containerRef} className='container-grid flex md:flex-row lg:grid flex-col'>


        <div ref={titleRef} className="flex md:flex-col lg:col-span-3 lg:p-0 md:pr-[3.2rem] md:mb-0 md:items-end md:gap-[2.4rem] lg:gap-[1.2rem] xl:gap-[2.4rem] md:justify-center w-full mb-[2.4rem] justify-between">
          {items.map((item, index) => (
            <button key={'0' + index} onClick={() => setCurrentIndex(index)} onMouseEnter={() => setCurrentIndex(index)} >
              <span
                className={`${
                  currentIndex === index
                    ? 'text-dusty-green-dark'
                    : 'text-dark-blue-20'
                } lg:text-[3.6rem] md:text-[2.4rem] heading-4 cursor-pointer transition-colors duration-1000 xl:text-[4.8rem]`}
              >
                {item.name}
              </span>
            </button>
          ))}
        </div>

        <div ref={imagesWrapper} className="w-full mb-[1.6rem] lg:col-start-5 lg:col-span-4 lg:w-full lg:h-auto md:w-auto md:h-[33rem] aspect-[1.2/1] md:aspect-[0.73/1] relative">
          {items.map((item, index) => (
            <div
              key={'1' + index}
              className={`${
                currentIndex === index ? 'opacity-100' : 'opacity-0'
              } w-full h-full transition-opacity duration-1000 overflow-hidden absolute`}
            >
              <Image
                src={item.image.url}
                alt={item.image?.alt}
                fill
                sizes="(max-width: 768px) 100vw,(max-width: 1280px) 30vw, 50vw"
                className="lg:scale-125 w-auto object-cover parallax-image"
              />
            </div>
          ))}
        </div>

        <div ref={textRef} className="w-full md:flex md:pl-[1.6rem] relative lg:col-start-10 lg:p-0 lg:col-span-3 md:items-center">
          {items.map((item, index) => (
            <div
              key={'2' + index}
              className={`${
                currentIndex === index ? 'flex lg:opacity-100' : 'hidden lg:flex lg:opacity-0'
              } flex-col w-full gap-[1.6rem] transition-opacity duration-1000 lg:w-full lg:absolute `}
            >
              {item.text ? <>
                {item.text.map((paragraph, pIndex) => (
                  <p key={'3' + pIndex} className="text-1  text-dark-blue">
                    {paragraph.text}
                  </p>
                ))}
              </> : null}

            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutPrinciples;
